import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Palette, Eye, Save, RotateCcw, Upload, Image } from 'lucide-react';

interface BannerConfig {
  title?: string;
  subtitle?: string;
  backgroundImage?: string;
  backgroundColor?: string;
  textColor?: string;
  ctaText?: string;
  ctaLink?: string;
}

interface BannerConfigManagerProps {
  initialConfig?: BannerConfig;
  onSave: (config: BannerConfig) => Promise<void>;
  loading?: boolean;
}

export const BannerConfigManager: React.FC<BannerConfigManagerProps> = ({
  initialConfig = {},
  onSave,
  loading = false
}) => {
  const { toast } = useToast();
  const [config, setConfig] = useState<BannerConfig>({
    title: 'Comprehensive Business Insurance',
    subtitle: 'Protect your business with our tailored insurance coverage designed for UAE enterprises, provided by our broker partner YallaCompare',
    backgroundColor: '#1E40AF',
    textColor: '#FFFFFF',
    ctaText: 'Get Your Quote',
    ctaLink: '/decision-tree',
    ...initialConfig
  });

  const [previewMode, setPreviewMode] = useState(false);

  const handleInputChange = (field: keyof BannerConfig, value: string) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      await onSave(config);
      toast({
        title: 'Banner Configuration Saved',
        description: 'Your banner settings have been updated successfully.',
      });
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Failed to save banner configuration',
        variant: 'destructive',
      });
    }
  };

  const handleReset = () => {
    setConfig({
      title: 'Comprehensive Business Insurance',
      subtitle: 'Protect your business with our tailored insurance coverage designed for UAE enterprises, provided by our broker partner YallaCompare',
      backgroundColor: '#1E40AF',
      textColor: '#FFFFFF',
      ctaText: 'Get Your Quote',
      ctaLink: '/decision-tree',
    });
  };

  const presetColors = [
    { name: 'Blue', bg: '#1E40AF', text: '#FFFFFF' },
    { name: 'Green', bg: '#059669', text: '#FFFFFF' },
    { name: 'Purple', bg: '#7C3AED', text: '#FFFFFF' },
    { name: 'Red', bg: '#DC2626', text: '#FFFFFF' },
    { name: 'Orange', bg: '#EA580C', text: '#FFFFFF' },
    { name: 'Teal', bg: '#0D9488', text: '#FFFFFF' },
    { name: 'Dark', bg: '#1F2937', text: '#FFFFFF' },
    { name: 'Light', bg: '#F9FAFB', text: '#1F2937' },
  ];

  return (
    <div className="space-y-6">
      {/* Configuration Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Banner Configuration
          </CardTitle>
          <CardDescription>
            Customize your homepage banner with your brand colors and messaging
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Text Content */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="title">Banner Title</Label>
              <Input
                id="title"
                value={config.title || ''}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter banner title"
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="subtitle">Banner Subtitle</Label>
              <Textarea
                id="subtitle"
                value={config.subtitle || ''}
                onChange={(e) => handleInputChange('subtitle', e.target.value)}
                placeholder="Enter banner subtitle/description"
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <Separator />

          {/* Colors */}
          <div className="space-y-4">
            <h4 className="font-medium">Color Scheme</h4>
            
            {/* Color Presets */}
            <div>
              <Label className="text-sm">Quick Presets</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {presetColors.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleInputChange('backgroundColor', preset.bg);
                      handleInputChange('textColor', preset.text);
                    }}
                    className="flex items-center gap-2"
                  >
                    <div 
                      className="w-4 h-4 rounded border"
                      style={{ backgroundColor: preset.bg }}
                    />
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>

            {/* Custom Colors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="backgroundColor">Background Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="backgroundColor"
                    type="color"
                    value={config.backgroundColor || '#1E40AF'}
                    onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={config.backgroundColor || '#1E40AF'}
                    onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                    placeholder="#1E40AF"
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="textColor">Text Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="textColor"
                    type="color"
                    value={config.textColor || '#FFFFFF'}
                    onChange={(e) => handleInputChange('textColor', e.target.value)}
                    className="w-16 h-10 p-1"
                  />
                  <Input
                    value={config.textColor || '#FFFFFF'}
                    onChange={(e) => handleInputChange('textColor', e.target.value)}
                    placeholder="#FFFFFF"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Call to Action */}
          <div className="space-y-4">
            <h4 className="font-medium">Call to Action</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ctaText">Button Text</Label>
                <Input
                  id="ctaText"
                  value={config.ctaText || ''}
                  onChange={(e) => handleInputChange('ctaText', e.target.value)}
                  placeholder="Get Your Quote"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="ctaLink">Button Link</Label>
                <Input
                  id="ctaLink"
                  value={config.ctaLink || ''}
                  onChange={(e) => handleInputChange('ctaLink', e.target.value)}
                  placeholder="/decision-tree"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Background Image */}
          <div className="space-y-4">
            <h4 className="font-medium">Background Image (Optional)</h4>
            <div>
              <Label htmlFor="backgroundImage">Image URL</Label>
              <Input
                id="backgroundImage"
                value={config.backgroundImage || ''}
                onChange={(e) => handleInputChange('backgroundImage', e.target.value)}
                placeholder="https://example.com/banner-bg.jpg"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty to use solid background color
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <Button onClick={handleSave} disabled={loading}>
              {loading ? (
                <>Loading...</>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Configuration
                </>
              )}
            </Button>

            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>

            <Button 
              variant="outline" 
              onClick={() => setPreviewMode(!previewMode)}
            >
              <Eye className="h-4 w-4 mr-2" />
              {previewMode ? 'Hide Preview' : 'Show Preview'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {previewMode && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Banner Preview
            </CardTitle>
            <CardDescription>
              Preview how your banner will look on the homepage
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div 
              className="relative rounded-lg overflow-hidden min-h-[300px] flex items-center justify-center p-8"
              style={{
                backgroundColor: config.backgroundColor,
                color: config.textColor,
                backgroundImage: config.backgroundImage ? `url(${config.backgroundImage})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {config.backgroundImage && (
                <div 
                  className="absolute inset-0 bg-black bg-opacity-40"
                  style={{ backgroundColor: `${config.backgroundColor}80` }}
                />
              )}
              
              <div className="relative text-center max-w-4xl">
                <div className="space-y-3 mb-6">
                  <h1 className="text-4xl md:text-5xl font-bold">
                    {config.title || 'Banner Title'}
                  </h1>

                  {/* Prominent YallaCompare Branding */}
                  <div className="flex items-center justify-center gap-2 opacity-90">
                    <div className="h-px bg-current flex-1 max-w-12"></div>
                    <p className="text-sm md:text-base font-medium tracking-wide">
                      Powered by YallaCompare Insurance Broker LLC #20
                    </p>
                    <div className="h-px bg-current flex-1 max-w-12"></div>
                  </div>
                </div>

                <p className="text-xl md:text-2xl mb-8 opacity-90">
                  {config.subtitle || 'Banner subtitle goes here'}
                </p>
                <Button
                  size="lg"
                  style={{
                    backgroundColor: config.textColor === '#FFFFFF' ? '#1F2937' : '#FFFFFF',
                    color: config.textColor === '#FFFFFF' ? '#FFFFFF' : '#1F2937',
                  }}
                >
                  {config.ctaText || 'Call to Action'}
                </Button>

                {/* Required Legal Text - Enhanced Visibility */}
                <div className="mt-8 pt-4 border-t border-white/20">
                  <p className="text-sm opacity-85 font-medium">
                    Powered by YallaCompare Insurance Broker LLC #20
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
